import { SearchOutlined } from '@ant-design/icons'
import { Checkbox, Input, Spin } from 'antd'
import { ExistingUserItem } from 'Modules'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { useDebounce } from 'hooks/use-debounce'
import { mapApiToExistingUser } from 'modules/promo-code/api-adapters'
import { logError } from 'utils/logger'

interface ExistingUsersSelectProps {
  /** Selected user ids (bound by Form.Item). */
  value?: string[]
  onChange?: (value: string[]) => void
  disabled?: boolean
}

const SCROLL_THRESHOLD_PX = 40
const SEARCH_DEBOUNCE_MS = 400

/**
 * "Existing users" whitelist picker: search KYC'd users by email or wallet
 * address and tick them into the whitelist. Backend-paged (sorted by email
 * A-Z), appending pages as the list scrolls.
 */
export const ExistingUsersSelect = ({ value, onChange, disabled }: ExistingUsersSelectProps) => {
  const selected = value ?? []
  const [keyword, setKeyword] = useState('')
  const debouncedKeyword = useDebounce(keyword.trim(), SEARCH_DEBOUNCE_MS)

  const [users, setUsers] = useState<ExistingUserItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const pageRef = useRef(DEFAULT_PAGE_INDEX)
  const isLoadingRef = useRef(false)

  const fetchUsers = useCallback(async (pageNum: number, search: string) => {
    if (isLoadingRef.current) return
    isLoadingRef.current = true
    setLoading(true)

    try {
      const res = await AppAdmin.getDistributionExistingUsers({
        page: pageNum,
        limit: DEFAULT_PAGE_SIZE,
        keyword: search || undefined,
        sort: 'email:asc',
      })
      const payload = res.data?.data
      const next = (payload?.results ?? []).map(mapApiToExistingUser)
      setUsers((prev) => (pageNum === DEFAULT_PAGE_INDEX ? next : [...prev, ...next]))
      setHasMore(pageNum < (payload?.totalPages ?? 0))
      pageRef.current = pageNum
    } catch (error) {
      logError('Error fetching existing users:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }, [])

  useEffect(() => {
    fetchUsers(DEFAULT_PAGE_INDEX, debouncedKeyword)
  }, [fetchUsers, debouncedKeyword])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    const nearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < SCROLL_THRESHOLD_PX
    if (nearBottom && hasMore && !isLoadingRef.current) {
      fetchUsers(pageRef.current + 1, debouncedKeyword)
    }
  }

  const toggleUser = (userId: string, checked: boolean) => {
    onChange?.(checked ? [...selected, userId] : selected.filter((id) => id !== userId))
  }

  return (
    <div className="whitelist-email__user-select">
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search by email or wallet address"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        allowClear
        disabled={disabled}
      />
      <div className="whitelist-email__user-list" onScroll={handleScroll}>
        {users.map((user) => (
          <Checkbox
            key={user.id}
            className="whitelist-email__user-row"
            checked={selected.includes(user.id)}
            disabled={disabled}
            onChange={(e) => toggleUser(user.id, e.target.checked)}
          >
            <span className="whitelist-email__user-email">{user.email}</span>
            <span className="whitelist-email__user-wallet">{user.walletAddress}</span>
          </Checkbox>
        ))}
        {loading && (
          <div className="whitelist-email__user-loading">
            <Spin size="small" />
          </div>
        )}
        {!loading && users.length === 0 && (
          <div className="whitelist-email__user-empty">No users found</div>
        )}
      </div>
      <div className="whitelist-email__user-count">
        {selected.length} user{selected.length === 1 ? '' : 's'} selected
      </div>
    </div>
  )
}
